import React from "react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";

import SvgCopy from "./svg/Copy";
import SvgDelete from "./svg/Delete";
import SvgDuplicate from "./svg/Duplicate";
import { SiteContext } from "../../../contexts";
import { usePage } from "../../../hooks";

import "./style.css";

/**
 * <Wrapper>
 */
const Wrapper = (props: WrapperProps) => {
	const componentRef = useRef<HTMLSpanElement>(null);
	const [wrapperHeight, setWrapperHeight] = useState(0);
	const { selectEditorID, selectHoverEditorID, moduleActions } =
		useContext(SiteContext);
	const { componentSchemas } = usePage();

	const {
		selectedEditorID,
		editorID,
		component,
		children,
		isSelectionAllowed,
		type,
	} = props;

	const isNavigationModule = !!type && ["header", "footer"].includes(type);

	const componentSchema = componentSchemas
		? Object.values(componentSchemas)
				.flatMap((group) => Object.values(group))
				.find((item) => item.component === component)
		: undefined;

	const { displayName, schemaType } = componentSchema || {};
	const isNotComponent = schemaType !== "component";

	// biome-ignore lint/correctness/useExhaustiveDependencies: fire with children change
	useEffect(() => {
		if (!!componentRef && componentRef.current) {
			setWrapperHeight(componentRef.current.scrollHeight);
		}
	}, [children]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: fire with componentRef change
	useEffect(() => {
		isSelected && scrollToComponent();
	}, [componentRef]);

	const cb = useCallback(
		(entries: IntersectionObserverEntry[]) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting && selectHoverEditorID && editorID) {
					selectHoverEditorID(editorID);
				}
			});
		},
		[selectHoverEditorID, editorID],
	);

	const ioConfiguration = {
		rootMargin: "-30% 0% -70% 0%",
		threshold: 0,
	};

	const observer = new IntersectionObserver(cb, ioConfiguration);

	// biome-ignore lint/correctness/useExhaustiveDependencies: fire only with that dependencies
	useEffect(() => {
		if (componentRef.current) {
			observer.observe(componentRef.current);
		}
		return () => {
			if (componentRef.current) {
				observer.unobserve(componentRef.current);
			}
		};
	}, [componentRef, observer]);

	const handleClick = (e: React.SyntheticEvent) => {
		if (
			(!editorID && editorID !== 0) ||
			typeof window === "undefined" ||
			!isSelectionAllowed
		) {
			return;
		}
		selectEditorID?.(props, undefined, e);
	};

	const scrollToComponent = () => {
		if (!componentRef.current) return;
		const pos = componentRef.current.style.position;
		const top = componentRef.current.style.top;
		componentRef.current.style.position = "relative";
		componentRef.current.style.top = "-34px";
		componentRef.current.scrollIntoView({
			behavior: "smooth",
			block: "nearest",
		});
		componentRef.current.style.top = top;
		componentRef.current.style.position = pos;

		const offsetTop = componentRef.current.offsetTop;
		if (offsetTop === 0) {
			componentRef.current.style.marginTop = "36px";
		}
	};

	const isSelected = !!selectedEditorID && selectedEditorID === editorID;
	isSelected && scrollToComponent();

	const removeItem = (e: React.SyntheticEvent) => {
		e.stopPropagation();
		moduleActions && editorID && moduleActions.deleteModuleAction(editorID);
	};

	const duplicateItem = (e: React.SyntheticEvent) => {
		e.stopPropagation();
		moduleActions && editorID && moduleActions.duplicateModuleAction(editorID);
	};

	const copyItem = (e: React.SyntheticEvent) => {
		e.stopPropagation();
		moduleActions && editorID && moduleActions.copyModuleAction(editorID);
	};

	const iconSize = isNotComponent ? 24 : 16;

	return (
		<>
			{component === "Header" && (
				<style
					// biome-ignore lint/security/noDangerouslySetInnerHtml: trusted html
					dangerouslySetInnerHTML={{
						__html: `
          [data-text="Header"].--selected::before {
            height: ${wrapperHeight - 8}px;
          }`,
					}}
				/>
			)}
			{isNavigationModule && (
				// biome-ignore lint/a11y/useSemanticElements: fix
				<span
					className={`${isSelected ? "--selected-header-footer" : ""}`}
					data-text={component}
					ref={componentRef}
					onClick={handleClick}
					onKeyDown={handleClick}
					role="button"
					tabIndex={0}
				>
					{children}
				</span>
			)}
			{!isNavigationModule && (
				// biome-ignore lint/a11y/useSemanticElements: fix
				<span
					className={`${isSelected ? "--selected" : ""}`}
					data-text={component}
					ref={componentRef}
					onClick={handleClick}
					onKeyDown={handleClick}
					role="button"
					tabIndex={0}
				>
					<span
						className={`${
							isSelected
								? `--selected-nameplate --selected-${schemaType}`
								: "--not-selected-nameplate"
						}`}
					>
						{isNotComponent && (displayName || component)}
						<button
							className="span-svg-action"
							onClick={copyItem}
							onKeyDown={copyItem}
							title="Copy"
							type="button"
							tabIndex={0}
						>
							<SvgCopy
								className="svg-action"
								width={iconSize}
								height={iconSize}
								viewBox="0 0 24 24"
							/>
						</button>
						<button
							className="span-svg-action"
							onClick={duplicateItem}
							onKeyDown={duplicateItem}
							title="Duplicate"
							type="button"
							tabIndex={0}
						>
							<SvgDuplicate
								className="svg-action"
								width={iconSize}
								height={iconSize}
								viewBox="0 0 24 24"
							/>
						</button>
						<button
							className="span-svg-action"
							onClick={removeItem}
							onKeyDown={removeItem}
							title="Delete"
							type="button"
							tabIndex={0}
						>
							<SvgDelete
								className="svg-action"
								width={iconSize}
								height={iconSize}
								viewBox="0 0 24 24"
							/>
						</button>
					</span>
					{children}
				</span>
			)}
		</>
	);
};

interface WrapperProps {
	selectedEditorID: number | null;
	editorID?: number;
	component: string;
	type?: string;
	parentEditorID?: number;
	isSelectionAllowed: boolean;
	children: JSX.Element;
}

export { Wrapper as ComponentWrapper };
